import React, { useState, useEffect } from 'react';
import { useField, useFormikContext } from 'formik';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import FieldWrapper from './wrapper/FieldWrapper';
import ErrorWrapper from './wrapper/ErrorWrapper';
import { P } from 'components/text';
import { UploadError } from 'lib/errors';

const getColor = (props) => {
  if (props.isDragActive) {
      return props.theme.cyan;
  }

  return props.theme.border;
}

const FileSelectWrapper = styled(FieldWrapper)`
  /* ability to toggle between showing and hiding the file input
  (even if a file is already selected) */
  display: ${props => props.hide ? "none" : "block"};
  padding: ${props => props.theme.paddingXS} 0;
`

const Dropzone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 80px;
  margin-top: ${props => props.theme.paddingXS};
  border: 2px dashed ${props => getColor(props)};
  border-radius: 5px;
  background-color: inherit;
  outline: none;
  transition: border .2s ease-in-out;

  &:focus {
    border-color: ${props => props.theme.cyan};
    outline: none;
  }

  input {
    box-shadow: none;
    padding: ${props => props.theme.paddingS};
  }

  p {
    margin: 0;
  }
`

const ThumbContainer = styled.aside`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: ${props => props.theme.paddingS};
`

const Thumb = styled.div`
  display: inline-flex;
  border-radius: 5px;
  border: 1px solid ${props => props.theme.border};
  width: 200px;
  height: 100px;
  padding: ${props => props.theme.paddingXS};
  box-sizing: border-box;
`

const ThumbInner = styled.div`
  display: flex;
  justify-content: center;
  min-width: 0;
  overflow: hidden;
`

const Preview = styled.img`
  display: block;
  width: auto;
  height: 100%;
`

const FileSelect = (props) => {
  const [field, meta] = useField(props);

  const { setFieldValue, setFieldError, setFieldTouched } = useFormikContext();

  const [filename, setFilename] = useState();
  const [filePreview, setFilePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const onDrop = ((acceptedFiles) => {
    acceptedFiles.forEach((file) => {
      const reader = new FileReader()

      reader.onabort = (e) => {
        throw new UploadError(e);
      }
      reader.onerror = (e) => {
        throw new UploadError(e);
      }
      reader.onload = async (item) => {
        setIsUploading(true)
        const response = await props.onChange(reader.result);

        if (response && response.status === 200) {
          setFilename(file.name);
          setFieldValue(field.name, file.name);
          setFilePreview(URL.createObjectURL(file));
        } else {
          setFieldTouched(field.name, true, false) // skip validation by passing a third argument as false
          setFieldError(field.name, "There was an error uploading your file. Please try again.")
        }

        setIsUploading(false)
      }

      reader.readAsArrayBuffer(file)
    })
  })

  const {
    getRootProps,
    getInputProps,
    isDragActive,
  } = useDropzone({
    multiple: false,
    accept: "image/*,application/pdf",
    onDrop,
  });

  // https://react-dropzone.js.org/#!/Previews
  useEffect(() => () => {
    // Make sure to revoke the data uris to avoid memory leaks
    URL.revokeObjectURL(filePreview);
  }, [filePreview]);

  const displayText = () => {
    if (isUploading) {
      return "Uploading..."
    } else if (filename) {
      return filename
    } else {
      return "Drag file here or click to select a file"
    }
  }

  return (
    <FileSelectWrapper htmlFor={props.name} hide={props.hide}>
      <strong>{props.label}</strong>
      <Dropzone {...getRootProps({
          isDragActive,
          onClick: evt => evt.stopPropagation(),  // prevent file select dialog from opening twice
        })}>
        <input
          {...getInputProps({
            capture:"environment", // allow using outward-facing camera
            id: props.name,
            name: props.name,
          })}
        />
        <P>
          { displayText() }
        </P>
      </Dropzone>
      { filePreview &&
        <ThumbContainer>
          <Thumb>
            <ThumbInner>
              <Preview
                src={filePreview}
                alt={`${filename} preview`}
              />
            </ThumbInner>
          </Thumb>
        </ThumbContainer>
      }
      { meta.touched && meta.error &&
        <ErrorWrapper name={props.name}>
          {meta.error}
        </ErrorWrapper>
      }
    </FileSelectWrapper>
  );
};

export default FileSelect;
